import React from 'react';
import { getTeeColor, teeNeedsOutline } from '../utils/teeColors';
import ScoreMarker from './ScoreMarker';
import './FullScorecardGrid.css';

/**
 * The Rounds → Full Scorecard card: ONE ROW PER PLAYER.
 *
 * Gross and Stableford points used to occupy a row each, so an eight-player
 * field was sixteen rows across two stacked tables. Here the points sit UNDER
 * the mark, in the same cell, and hole / par / SI fold into the navy header
 * band instead of taking three body rows of their own.
 *
 * THE POINTS ARE NOT INSIDE THE MARKER. They were, briefly, and it cost the
 * notation its shape: a mark's height is its content, so two stacked lines in a
 * cell narrower than they are tall beat `aspect-ratio: 1` and every circle
 * rendered as an ellipse — measured at 21.4 x 24 on a 360px phone. Outside the
 * mark, the mark is only ever the gross digit, its height is its width, and a
 * birdie is a circle at every width.
 *
 * The notation itself is NOT redefined here. Every hole is a `ScoreMarker`,
 * the same component the leaderboard's hole-by-hole card draws, so the same
 * round cannot look different on the two pages.
 *
 * ONE CARD PER TEE. Par and stroke index belong to the tee, so a mixed-tee
 * round cannot describe its field with a single header band; the page renders
 * one of these per `teeSection` and passes `showTeeLabel`. A single-tee round
 * gets exactly one card.
 *
 * `locked` IS THE FINALISED ROUND, and it is enforced here rather than by CSS.
 * The grid this replaces relied on `pointer-events: none` from a `.disabled`
 * class to stop a finalised round being edited — a presentational rule holding
 * a data guarantee, one stylesheet change away from letting a tap re-open a
 * locked score. Every cell is genuinely read-only instead; the card stays at
 * full strength, because a finalised round is the thing this screen exists to
 * show and dimming it to half opacity made it hard to read.
 */

// Initials, the way the old grid's player stub did it — the card is nine
// columns wide and a full name has never fitted.
export const initialsOf = (name) => (name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part[0].toUpperCase())
  .join('') || '—';

// A ZERO IS A SCORE. `total || '-'` is what the old grid did, and it turned a
// legitimate nought — a player who wiped every hole, which off a low handicap
// is entirely possible — into "no card". Only an absent total is a dash.
export const figure = (v) => (v === '' || v === null || v === undefined ? '-' : String(v));

// A plus handicap reads as (+2), a scratch player as (0). The stored value is
// negative for plus, which is the convention every other screen displays too.
const handicapLabel = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return '(0)';
  return n < 0 ? `(+${Math.abs(n)})` : `(${n})`;
};

const Cell = React.memo(({
  player, holeNumber, score, par, points, readOnly, inPlay, onCellClick,
}) => {
  return (
    <div
      className={`fsc-cell${inPlay ? '' : ' fsc-cell--out'}${readOnly ? ' fsc-cell--locked' : ''}`}
      onClick={readOnly ? undefined : () => onCellClick(player, holeNumber)}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onKeyDown={readOnly ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCellClick(player, holeNumber); }
      }}
    >
      <ScoreMarker strokes={score} par={par} className="fsc-mark" />
      {/* An unplayed hole shows no points line at all — a 0 there reads as a
          wipe, which is a different thing from "not yet played". The slot is
          held open either way so the marks stay on one baseline down a column. */}
      <span className="fsc-cell-pts">{score ? points : ''}</span>
    </div>
  );
});

const FullScorecardGrid = ({
  section,
  base = 0,
  holeCount = 9,
  scores,
  playerTotals,
  handicapFor,
  pointsFor,
  editableIds,
  playableHoles,
  onCellClick,
  locked = false,
  showTeeLabel = false,
  totalLabel,
}) => {
  const holes = [];
  for (let i = base; i < base + holeCount; i += 1) holes.push({ index: i, ...(section.holes[i] || {}) });

  // OUT for the front, IN for the back, TOT for a whole-18 (desktop) card.
  const label = totalLabel || (holeCount > 9 ? 'Tot' : (base === 0 ? 'Out' : 'In'));

  const totalKey = holeCount > 9 ? 'total' : (base === 0 ? 'front9' : 'back9');

  return (
    <div
      className={`fsc-card${holeCount > 9 ? ' fsc-card--wide' : ''}${locked ? ' fsc-card--locked' : ''}`}
      style={{ '--fsc-holes': holeCount }}
    >
      {/* WHICH TEE, ON ITS OWN LINE. It was in the header stub first, and
          measured: 52px cannot hold a dot and the word "Yellow", so every tee
          name ellipsised at every width. A caption across the card fits any
          name, and on a mixed-tee day it is the first thing you need to read
          anyway — the par and SI below it are only true for this tee. */}
      {showTeeLabel && (
        <div className="fsc-tee-cap">
          <span
            className={`fsc-tee-cap-dot${teeNeedsOutline(section.tee?.name) ? ' fsc-tee-cap-dot--outlined' : ''}`}
            style={{ backgroundColor: getTeeColor(section.tee?.name) || '#ffffff' }}
          />
          <span className="fsc-tee-cap-name">{section.tee?.name || 'Tee'}</span>
          <span className="fsc-tee-cap-count">
            {section.players.length} player{section.players.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      <div className={`fsc-head${showTeeLabel ? ' fsc-head--capped' : ''}`}>
        <div className="fsc-head-stub">
          <span className="fsc-head-stub-line fsc-head-stub-line--hole">Hole</span>
          <span className="fsc-head-stub-line fsc-head-stub-line--par">Par</span>
          <span className="fsc-head-stub-line fsc-head-stub-line--si">SI</span>
        </div>

        {holes.map(h => (
          <div className="fsc-head-col" key={`h-${h.index}`}>
            <span className="fsc-head-hole">{h.index + 1}</span>
            <span className="fsc-head-par">{h.par ?? '-'}</span>
            <span className="fsc-head-si">{h.strokeIndex ?? '-'}</span>
          </div>
        ))}

        <div className="fsc-head-total">{label}</div>
      </div>

      {section.players.map(player => {
        const hcp = handicapFor(player.id);
        const totals = playerTotals[player.id] || {};
        return (
          <div className="fsc-row" key={`fsc-${player.id}`}>
            <div className="fsc-row-stub">
              <span className="fsc-row-initials">{initialsOf(player.name)}</span>
              <span className="fsc-row-hcp">{handicapLabel(hcp)}</span>
            </div>

            {holes.map(h => {
              const holeNumber = h.index + 1;
              const score = scores[player.id]?.[holeNumber];
              return (
                <Cell
                  key={`c-${player.id}-${holeNumber}`}
                  player={player}
                  holeNumber={holeNumber}
                  score={score}
                  par={h.par}
                  points={pointsFor(score, h.par, h.strokeIndex, hcp)}
                  inPlay={playableHoles.has(holeNumber)}
                  readOnly={locked || !editableIds.has(player.id) || !playableHoles.has(holeNumber)}
                  onCellClick={onCellClick}
                />
              );
            })}

            <div className="fsc-row-total">
              <span className="fsc-row-total-gross">{figure(totals[`${totalKey}Gross`])}</span>
              <span className="fsc-row-total-pts">{figure(totals[`${totalKey}Stableford`])}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FullScorecardGrid;
